export interface ContentBlock {
  type: "h2" | "h3" | "p" | "quote" | "list";
  text?: string;
  items?: string[];
}

export interface Article {
  id: string;
  kind: "morning" | "gossip";
  category: string;
  title: string;
  excerpt: string;
  cover: string;
  date: string;
  author: string;
  readTime: string;
  tags: string[];
  content: ContentBlock[];
}

export const articleCategories = [
  "剧集观察",
  "综艺幕后",
  "音乐现场",
  "行业风向",
  "平台生态",
];

export const articles: Article[] = [
  {
    id: "huan-jiao-bei-hou",
    kind: "gossip",
    category: "剧集观察",
    title: "一部古装大剧的「临阵换角」，到底在换什么",
    excerpt:
      "开机前两周更换主演，对外只说「档期原因」。但在剧组里，换角往往不是一个人的事，而是一整条制作链路的重新排布。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20editorial%20still%20of%20a%20traditional%20Chinese%20film%20set%20with%20red%20lanterns%2C%20wooden%20props%20and%20soft%20golden%20afternoon%20light%2C%20cinematic%20composition%2C%20muted%20crimson%20and%20cream%20tones%2C%20no%20people&width=1200&height=800&seq=hl-gj-01&orientation=landscape",
    date: "2026-09-12",
    author: "编辑部 · 阿栗",
    readTime: "6 分钟",
    tags: ["换角", "剧组", "档期", "制作"],
    content: [
      {
        type: "p",
        text: "「临阵换角」是影视行业里最容易被简化成一句话的事：某位演员因为档期退出，另一位紧急补位。但真正走进剧组你会发现，换掉的从来不只是一个名字，而是一整套已经排好的时间表。",
      },
      { type: "h2", text: "一、换角的第一张多米诺骨牌" },
      {
        type: "p",
        text: "剧组的开机日往往是倒推出来的：平台档期、宣发窗口、演员档期、服化道交付时间，四张时间表必须对得上。只要其中一张挪动，其余三张都要跟着调整。主演一旦更换，最直接的问题不是演技，而是「身形数据」——戏服、头套、道具尺寸都要重新打样。",
      },
      {
        type: "p",
        text: "很多观众以为服装是现成的，实际上主要角色的戏服往往是量身定制，一套古装从选料到成衣通常需要三到四周。换角意味着这批成衣大概率报废，服化组的加班从这里开始。",
      },
      { type: "h2", text: "二、真正的谈判发生在合同附页" },
      {
        type: "p",
        text: "外界看到的「档期原因」，往往只是对外说辞。真正让制作方下决心的，通常是合同附页里的几行字：番位排序、宣传配合次数、衍生品分成、以及最关键的——可调整档期天数。",
      },
      {
        type: "quote",
        text: "换角不是换演员，是换一整套风险分配方案。",
      },
      {
        type: "list",
        items: [
          "若新演员可调整档期天数更多，剧组就能把补拍风险后移；",
          "若宣传配合次数更高，宣发预算可以相应压缩；",
          "若番位更灵活，海报与片花的分镜都能重排。",
        ],
      },
      { type: "h2", text: "三、观众该关心什么" },
      {
        type: "p",
        text: "对观众来说，换角本身不值得恐慌。真正影响成片质量的，是制作方有没有为这次调整留出足够的准备期。留够了，角色反而可能更贴；没留够，观众在成片里看到的将是被压缩的镜头与赶工的布景。",
      },
      {
        type: "p",
        text: "所以下次再看到「因档期退出」的公告，不妨多留意一句：补位演员进组那天，距离原定开机还有多久。这个数字，比任何解释都诚实。",
      },
    ],
  },
  {
    id: "jian-ji-san-ci-bo-yi",
    kind: "gossip",
    category: "综艺幕后",
    title: "综艺剪辑室里的三次博弈",
    excerpt:
      "一档综艺的最终样子，是在剪辑室里被反复「谈判」出来的。嘉宾、导演组、平台三方各有诉求，而观众看到的每一分钟，都是妥协的结果。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20moody%20editing%20suite%20with%20multiple%20monitors%20showing%20timeline%20and%20footage%2C%20amber%20desk%20lamp%20glow%2C%20dark%20walnut%20furniture%2C%20cinematic%20depth%20of%20field%2C%20muted%20crimson%20accents%2C%20no%20people&width=1200&height=800&seq=hl-gj-02&orientation=landscape",
    date: "2026-09-11",
    author: "编辑部 · 老白",
    readTime: "7 分钟",
    tags: ["综艺", "剪辑", "剪辑室", "博弈"],
    content: [
      {
        type: "p",
        text: "很多人以为综艺是「拍出来的」，其实它更像是「剪出来的」。同一批素材，不同的剪辑逻辑会得到完全不同的节目。剪辑室因此成了一个微妙的谈判场。",
      },
      { type: "h2", text: "第一次博弈：嘉宾的形象线" },
      {
        type: "p",
        text: "嘉宾经纪团队会关注自己在正片里的「人设走向」。持续的高光可能招来争议，持续的低调又担心曝光不足。剪辑师要在有限时长里，让每个人都有一个「能被记住的瞬间」。",
      },
      { type: "h2", text: "第二次博弈：导演的叙事" },
      {
        type: "p",
        text: "导演组在意的是节目整体的节奏。他们会为了保护某个反转，甘愿牺牲前二十分钟的精彩片段。这种取舍在观众看来可能「平平无奇」，却是叙事成立的必要代价。",
      },
      { type: "h2", text: "第三次博弈：平台的留存曲线" },
      {
        type: "p",
        text: "平台关注的是完播率和跳出点。当数据显示某一段观众大量流失，剪辑会被要求「提前给点东西」。这就是为什么很多综艺开头越来越密、钩子越来越早。",
      },
      {
        type: "quote",
        text: "观众看到的是成品，剪辑师看到的是三百次版本更迭。",
      },
      {
        type: "p",
        text: "理解这三次博弈，也就能理解为什么同一档节目，有人觉得很真实，有人觉得很刻意——它本来就是多方拉扯之后的平衡点。",
      },
    ],
  },
  {
    id: "yan-chang-hui-cheng-ben",
    kind: "gossip",
    category: "音乐现场",
    title: "一场演唱会的成本，到底花在哪儿",
    excerpt:
      "票面价格只讲了一半的故事。从场馆租金到升降台，从安保到荧光棒回收，一场大型演出的钱，流向了远比想象更长的一串清单。",
    cover:
      "https://readdy.ai/api/search-image?query=Empty%20concert%20stage%20before%20showtime%20with%20warm%20amber%20spotlights%2C%20truss%20structure%20and%20seating%20rows%2C%20cinematic%20wide%20shot%2C%20crimson%20and%20gold%20lighting%20haze%2C%20no%20people&width=1200&height=800&seq=hl-gj-03&orientation=landscape",
    date: "2026-09-10",
    author: "编辑部 · 江野",
    readTime: "5 分钟",
    tags: ["演唱会", "成本", "场馆", "巡演"],
    content: [
      {
        type: "p",
        text: "「一张票怎么这么贵」是演唱会后最常见的疑问。但把成本拆开看，你会发现票价只是这场演出的收入端之一，而支出端远比想象复杂。",
      },
      { type: "h2", text: "固定成本：不管坐满没坐满都要付" },
      {
        type: "list",
        items: [
          "场馆租金：通常按天计费，装台、演出、拆台各算一天；",
          "舞台搭建：桁架、升降台、LED 屏，运输与搭建都是大头；",
          "技术人员：灯光、音响、视频、机械，一趟巡演要跟一整队人。",
        ],
      },
      { type: "h2", text: "浮动成本：跟着场次走" },
      {
        type: "p",
        text: "安保、检票、志愿者、临时座椅、物料运输，这些随场次增加而线性增长。场次越多，单场分摊的固定成本越低，这也是巡演要「拉长线路」的原因。",
      },
      { type: "h2", text: "被忽略的回收项" },
      {
        type: "p",
        text: "荧光棒、应援物、场刊，既是周边收入，也是散场后的清理成本。有的主办方会设置回收点，既省钱又减少垃圾清运费用。",
      },
      {
        type: "p",
        text: "理解了成本结构，也就理解了为什么有些城市「加场」、有些城市「只来一次」——决定因素从来不是人气，而是场馆条件与运输半径。",
      },
    ],
  },
  {
    id: "duan-ju-qi-tian-cheng-ming",
    kind: "gossip",
    category: "行业风向",
    title: "短剧演员的「七天成名」与快速谢幕",
    excerpt:
      "一个月拍完整部戏、一部剧决定下一份合同，短剧的生产节奏重塑了演员的职业路径。快，是机会，也是消耗。",
    cover:
      "https://readdy.ai/api/search-image?query=Vertical%20phone%20tripod%20and%20ring%20light%20on%20a%20compact%20film%20set%20with%20warm%20cream%20backdrop%2C%20minimal%20props%2C%20small%20crew%20setup%2C%20cinematic%20close%20composition%2C%20crimson%20accent%20tones%2C%20no%20people&width=1200&height=800&seq=hl-gj-04&orientation=landscape",
    date: "2026-09-09",
    author: "编辑部 · 阿栗",
    readTime: "6 分钟",
    tags: ["短剧", "演员", "生产节奏", "合同"],
    content: [
      {
        type: "p",
        text: "传统剧集一部戏拍三四个月，短剧可能只需要十几天。这种压缩把「机会」和「消耗」同时放大。",
      },
      { type: "h2", text: "快在哪里" },
      {
        type: "p",
        text: "短剧的镜头语言高度标准化：固定机位为主，场景复用率高，台词密度大。演员进组第二天就要进入状态，没有太多磨合期。",
      },
      { type: "h2", text: "成名与谢幕" },
      {
        type: "p",
        text: "一部爆款短剧能在几天内带来大量关注，但热度衰减也快。这促使很多演员采取「高频接戏」策略：趁热度还在，尽快锁定下一份合同。",
      },
      {
        type: "quote",
        text: "在短剧的节奏里，作品不是履历，是流水线上的零件。",
      },
      {
        type: "p",
        text: "高频接戏的代价是角色同质化。真正能沉淀下来的演员，往往会在某个节点主动「减速」，用一部有时间打磨的作品完成转型。",
      },
    ],
  },
  {
    id: "hui-yuan-zhang-jia-suan-pan",
    kind: "gossip",
    category: "平台生态",
    title: "会员涨价背后：平台的算盘与用户的耐心",
    excerpt:
      "涨价的从来不只是价格，还有内容结构、广告位和会员权益的重新分配。用户的耐心，正成为平台估值模型里最敏感的一环。",
    cover:
      "https://readdy.ai/api/search-image?query=Cozy%20living%20room%20couch%20with%20a%20laptop%20showing%20a%20subscription%20page%2C%20warm%20cream%20blanket%20and%20amber%20lamp%20light%2C%20minimal%20scandinavian%20interior%2C%20soft%20shadow%2C%20crimson%20accent%20cushion%2C%20no%20people&width=1200&height=800&seq=hl-gj-05&orientation=landscape",
    date: "2026-09-08",
    author: "编辑部 · 老白",
    readTime: "6 分钟",
    tags: ["会员", "订阅", "广告", "平台"],
    content: [
      {
        type: "p",
        text: "每当视频平台调整会员价格，评论区总会分成两派。这背后其实是一个简单的算术题：内容成本在涨，而用户能接受的价格有上限。",
      },
      { type: "h2", text: "涨价的三种方式" },
      {
        type: "list",
        items: [
          "直接调高会员定价；",
          "拆分权益，把「超前点播」「高码率」单列；",
          "增加广告位，用注意力换价格。",
        ],
      },
      {
        type: "p",
        text: "明面上的涨价容易引发反弹，所以第三种方式被频繁使用。它的成本不容易被量化，却真实地消耗着用户耐心。",
      },
      { type: "h2", text: "用户耐心是一条曲线" },
      {
        type: "p",
        text: "平台内部通常用「取消率」和「续费意愿」衡量这条曲线。短期看，加广告能提升收入；长期看，一旦续费意愿下滑，恢复的成本远高于当初多赚的那点广告费。",
      },
      {
        type: "p",
        text: "这也是为什么头部平台越来越重视「独占内容」——它直接决定了用户是否愿意留下，而不是仅仅比较价格。",
      },
    ],
  },
  {
    id: "ju-zu-he-fan-jing-ji-xue",
    kind: "gossip",
    category: "行业风向",
    title: "剧组的盒饭经济学：一天三百份怎么不出错",
    excerpt:
      "盒饭是剧组里最不起眼、又最容易出问题的环节。几百人的口味、存储、分发与成本，构成了一套被低估的后勤系统。",
    cover:
      "https://readdy.ai/api/search-image?query=Neat%20rows%20of%20takeout%20meal%20boxes%20on%20a%20long%20wooden%20table%20with%20warm%20afternoon%20light%2C%20fresh%20vegetables%20and%20rice%20dishes%20in%20paper%20containers%2C%20warm%20cream%20and%20crimson%20accents%2C%20no%20people&width=1200&height=800&seq=hl-gj-06&orientation=landscape",
    date: "2026-09-07",
    author: "编辑部 · 江野",
    readTime: "5 分钟",
    tags: ["剧组", "后勤", "盒饭", "成本"],
    content: [
      {
        type: "p",
        text: "在外景地，一天发放三百份盒饭是常态。看似简单，但要同时满足口味、时间、温度和预算，并不容易。",
      },
      { type: "h2", text: "一次出错，代价是全组停工" },
      {
        type: "p",
        text: "盒饭迟到，直接导致拍摄延误。因此后勤组通常会在附近准备两家以上的供应商，随时可以切换。",
      },
      { type: "h2", text: "成本控制在看不见的地方" },
      {
        type: "list",
        items: [
          "批量采购主食与蔬菜，压低单份成本；",
          "按拍摄强度调整菜品，夜戏增加热汤；",
          "设置少量「加餐名额」，照顾体力消耗大的工种。",
        ],
      },
      {
        type: "p",
        text: "这些细节不会出现在成片里，却决定了剧组能不能按时收工。影视行业的很多「意外」，其实都藏在这些后勤环节里。",
      },
    ],
  },
  {
    id: "zao-bao-duanju-chuhai",
    kind: "morning",
    category: "行业风向",
    title: "短剧出海新动向：东南亚成为新一轮试水市场",
    excerpt:
      "多家制作公司把首站从欧美转向东南亚，本地化配音与竖屏节奏成为关键变量。",
    cover:
      "https://readdy.ai/api/search-image?query=Modern%20minimal%20desk%20with%20smartphone%20showing%20vertical%20video%20and%20notebook%2C%20warm%20cream%20background%2C%20amber%20lamp%20light%2C%20clean%20editorial%20product%20shot%2C%20crimson%20pen%20accent%2C%20no%20people&width=1000&height=700&seq=hl-zb-01&orientation=landscape",
    date: "2026-09-12",
    author: "早报组",
    readTime: "3 分钟",
    tags: ["短剧", "出海", "本地化"],
    content: [
      {
        type: "p",
        text: "上午的行业沟通会上，多家制作公司提到同一个方向：把东南亚作为短剧出海的下一站。相比欧美市场，东南亚的用户习惯更接近国内竖屏内容，本地化成本也更可控。",
      },
      { type: "h2", text: "关键变量" },
      {
        type: "list",
        items: [
          "配音与本字幕是基础门槛，泰语、印尼语需求上升；",
          "竖屏节奏需要重新剪辑，前 5 秒决定留存；",
          "支付与结算链路仍在完善中。",
        ],
      },
      {
        type: "p",
        text: "有从业者判断，真正的竞争点不在内容本身，而在「上线速度」——谁能更快完成本地化，谁就能先占住推荐位。",
      },
    ],
  },
  {
    id: "zao-bao-yinyuejie-tuipiao",
    kind: "morning",
    category: "音乐现场",
    title: "音乐节退票新规开始试行，主办方反应不一",
    excerpt:
      "分级退票机制在部分场次落地，观众与主办方对「不可抗力」的界定仍存分歧。",
    cover:
      "https://readdy.ai/api/search-image?query=Outdoor%20music%20festival%20grounds%20in%20late%20afternoon%20with%20stage%20scaffolding%20and%20warm%20amber%20sunlight%2C%20open%20field%20and%20string%20lights%2C%20cinematic%20wide%20composition%2C%20crimson%20and%20cream%20tones%2C%20no%20people&width=1000&height=700&seq=hl-zb-02&orientation=landscape",
    date: "2026-09-12",
    author: "早报组",
    readTime: "3 分钟",
    tags: ["音乐节", "退票", "规则"],
    content: [
      {
        type: "p",
        text: "针对音乐节退票的阶梯式新规在部分场次开始试行：越接近开演，退票比例越低。主办方表示，这有助于减少临期大量退票带来的排期风险。",
      },
      {
        type: "p",
        text: "不过观众更关心的，是「不可抗力」如何界定。天气、艺人档期、场地变更等情况是否适用，目前各家执行口径并不统一，后续可能还需要更细的行业共识。",
      },
    ],
  },
  {
    id: "zao-bao-zongyi-zhaoshang",
    kind: "morning",
    category: "综艺幕后",
    title: "综艺招商回暖，品牌冠名预算回到去年水平",
    excerpt:
      "多个头部综艺拿到新一轮冠名，品牌更看重「话题延续性」而非单期曝光。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20modern%20meeting%20room%20with%20charts%20on%20a%20screen%20and%20coffee%20cups%2C%20cream%20walls%20and%20walnut%20table%2C%20amber%20lighting%2C%20editorial%20business%20photography%2C%20crimson%20accent%2C%20no%20people&width=1000&height=700&seq=hl-zb-03&orientation=landscape",
    date: "2026-09-11",
    author: "早报组",
    readTime: "2 分钟",
    tags: ["综艺", "招商", "冠名"],
    content: [
      {
        type: "p",
        text: "下半年综艺招商出现回暖迹象，多档头部节目完成新一轮冠名签约。品牌方的反馈是：与其追求单期曝光量，不如选择能持续产出话题的节目。",
      },
      {
        type: "p",
        text: "这意味着节目组在策划阶段就要考虑「可被讨论的节点」——一个能形成二创、延展到社交平台的设定，比单纯的明星阵容更受青睐。",
      },
    ],
  },
  {
    id: "zao-bao-yingshi-jidi",
    kind: "morning",
    category: "剧集观察",
    title: "影视基地迎来剧组扎堆，档期需提前两个月预定",
    excerpt:
      "多个热门取景地排期紧张，部分剧组转向周边中小型基地以缩短等待时间。",
    cover:
      "https://readdy.ai/api/search-image?query=Traditional%20Chinese%20film%20town%20street%20with%20wooden%20facades%20and%20red%20lanterns%20in%20warm%20morning%20light%2C%20empty%20quiet%20alleys%2C%20cinematic%20wide%20shot%2C%20cream%20and%20crimson%20tones%2C%20no%20people&width=1000&height=700&seq=hl-zb-04&orientation=landscape",
    date: "2026-09-11",
    author: "早报组",
    readTime: "2 分钟",
    tags: ["影视基地", "取景", "排期"],
    content: [
      {
        type: "p",
        text: "进入秋季拍摄季，多个影视基地出现剧组扎堆的情况，热门场景的档期需要提前约两个月预定。",
      },
      {
        type: "p",
        text: "为缩短等待时间，一些中小成本剧组开始转向周边基地。虽然场景辨识度略低，但拍摄效率明显提升，综合成本反而更可控。",
      },
    ],
  },
  {
    id: "zao-bao-fenzhang-moshi",
    kind: "morning",
    category: "平台生态",
    title: "分账模式调整引发讨论，中小制作方态度分化",
    excerpt:
      "新的结算周期与分账比例同步调整，现金流健康的中小团队更能适应变化。",
    cover:
      "https://readdy.ai/api/search-image?query=Minimal%20office%20desk%20with%20calculator%2C%20paper%20documents%20and%20a%20cup%20of%20tea%2C%20warm%20cream%20background%2C%20soft%20natural%20light%2C%20editorial%20still%20life%2C%20crimson%20folder%20accent%2C%20no%20people&width=1000&height=700&seq=hl-zb-05&orientation=landscape",
    date: "2026-09-10",
    author: "早报组",
    readTime: "3 分钟",
    tags: ["分账", "结算", "制作方"],
    content: [
      {
        type: "p",
        text: "分账规则调整后，中小制作方的态度出现分化。现金流健康的团队普遍表示可以接受，而依赖单项目回款的团队则更为谨慎。",
      },
      {
        type: "p",
        text: "业内人士提醒，与其纠结比例数字，不如关注「结算周期」——它才是决定团队能否持续生产的关键变量。",
      },
    ],
  },
  {
    id: "zao-bao-ziying-ju",
    kind: "morning",
    category: "剧集观察",
    title: "自制剧立项数量上升，题材向现实向倾斜",
    excerpt:
      "平台自制剧明显增加，现实题材占比提升，制作周期普遍拉长。",
    cover:
      "https://readdy.ai/api/search-image?query=Stack%20of%20film%20scripts%20and%20a%20clapperboard%20on%20a%20wooden%20desk%20with%20warm%20cream%20wall%2C%20amber%20reading%20lamp%2C%20editorial%20composition%2C%20crimson%20accent%2C%20no%20people&width=1000&height=700&seq=hl-zb-06&orientation=landscape",
    date: "2026-09-10",
    author: "早报组",
    readTime: "2 分钟",
    tags: ["自制剧", "现实题材", "立项"],
    content: [
      {
        type: "p",
        text: "近期立项清单显示，平台自制剧数量继续上升，现实题材的占比明显提高，古装项目则相对收缩。",
      },
      {
        type: "p",
        text: "与之对应的是制作周期的拉长。现实题材对采风、调研与角色塑造的要求更高，前期准备时间普遍比过去多了三到四周。",
      },
    ],
  },
  {
    id: "zao-bao-yanchu-anbao",
    kind: "morning",
    category: "音乐现场",
    title: "大型演出安保标准细化，入场验票效率同步提升",
    excerpt:
      "新标准对通道宽度、疏散标识与人员配置做出更细规定，大型场馆改造需求上升。",
    cover:
      "https://readdy.ai/api/search-image?query=Wide%20arena%20entrance%20with%20neat%20queue%20barriers%20and%20soft%20amber%20evening%20light%2C%20clean%20architectural%20lines%2C%20cinematic%20composition%2C%20cream%20and%20crimson%20tones%2C%20no%20people&width=1000&height=700&seq=hl-zb-07&orientation=landscape",
    date: "2026-09-09",
    author: "早报组",
    readTime: "2 分钟",
    tags: ["安保", "大型演出", "场馆"],
    content: [
      {
        type: "p",
        text: "大型演出的安保标准进一步细化，对通道宽度、疏散标识和工作人员配比提出了更明确的要求。",
      },
      {
        type: "p",
        text: "对主办方而言，短期改造成本上升，但验票与入场效率也随之提升。观众的实际感受是：排队时间变短，散场更顺畅。",
      },
    ],
  },
  {
    id: "zao-bao-duanju-pingtai",
    kind: "morning",
    category: "平台生态",
    title: "短剧平台密集上线新功能，试看与追更成标配",
    excerpt:
      "围绕留存设计的功能竞赛加剧，创作者侧的分成工具也在同步更新。",
    cover:
      "https://readdy.ai/api/search-image?query=Three%20smartphones%20arranged%20on%20a%20cream%20surface%20showing%20vertical%20video%20interfaces%2C%20warm%20amber%20lighting%2C%20clean%20product%20composition%2C%20crimson%20accent%20cards%2C%20no%20people&width=1000&height=700&seq=hl-zb-08&orientation=landscape",
    date: "2026-09-09",
    author: "早报组",
    readTime: "2 分钟",
    tags: ["短剧平台", "留存", "功能"],
    content: [
      {
        type: "p",
        text: "短剧平台近期密集上线新功能，「免费试看若干集 + 追更提醒」几乎成为标配。",
      },
      {
        type: "p",
        text: "与此同时，面向创作者的分成与结算工具也在更新。可以预见，下一轮竞争将从内容供给转向「留存设计」的精细化程度。",
      },
    ],
  },
];

export const getArticleById = (id: string): Article | undefined =>
  articles.find((item) => item.id === id);

export const gossipArticles = articles.filter((item) => item.kind === "gossip");

export const morningArticles = articles.filter((item) => item.kind === "morning");